import { useTheme } from "@emotion/react";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Popup from "../../components/popups/popup";
import CustomInput from "../../components/inputs/customInput";
import AddIcon from '@mui/icons-material/Add';
import UserDropdown from "../../components/dropdowns/userDropdown";
import NotificationService from "../../services/notificationService";
import ReciverDropdown from "../../components/dropdowns/reciverDropdown";
import PageDropdown from "../../components/dropdowns/pageDropdown";
import DriverDropdown from "../../components/dropdowns/driverDropdown";

const AddNotification = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedReciver, setSelectedReciver] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPage, setSelectedPage] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const [formValues, setFormValues] = useState({
        reciver: '',
        reciverId: null,
        titleKurdish: '',
        titleEnglish: '',
        titleArabic: '',
        descriptionKurdish: '',
        descriptionEnglish: '',
        descriptionArabic: ''
    });

    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.reciver) newAddErrors.reciver = t('reciverRequired');
        if (!formValues.titleKurdish) newAddErrors.titleKurdish = t('reciverRequired');
        if (!formValues.titleEnglish) newAddErrors.titleEnglish = t('reciverRequired');
        if (!formValues.titleArabic) newAddErrors.titleArabic = t('reciverRequired');
        if (!formValues.descriptionKurdish) newAddErrors.descriptionKurdish = t('reciverRequired');
        if (!formValues.descriptionEnglish) newAddErrors.descriptionEnglish = t('reciverRequired');
        if (!formValues.descriptionArabic) newAddErrors.descriptionArabic = t('reciverRequired');


        setAddErrors(newAddErrors);

        return Object.keys(newAddErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                let reciverId;
                if (selectedReciver === t('page')) reciverId = selectedPage?.id;
                else if (selectedReciver === t('user')) reciverId = selectedUser?.id;
                else if (selectedReciver === t('driver')) reciverId = selectedDriver?.id;

                const data = await NotificationService.add({
                    ...formValues,
                    reciverId
                });

                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    setFormValues({
                        reciver: '',
                        reciverId: null,
                        titleKurdish: '',
                        titleEnglish: '',
                        titleArabic: '',
                        descriptionKurdish: '',
                        descriptionEnglish: '',
                        descriptionArabic: ''
                    });
                    setSelectedReciver(null);
                    setSelectedUser(null);
                    setSelectedDriver(null);
                    setSelectedPage(null);
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const personDropdown = () => {
        if (selectedReciver === t('page')) {
            return (
                <PageDropdown
                    helperText={addErrors.page}
                    error={!!addErrors.page}
                    isFilter={false}
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                />
            );
        } else if (selectedReciver === t('user')) {
            return (
                <UserDropdown
                    helperText={addErrors.user}
                    error={!!addErrors.user}
                    isFilter={false}
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                />
            );
        } else if (selectedReciver === t('driver')) {
            return (
                <DriverDropdown
                    helperText={addErrors.driver}
                    error={!!addErrors.driver}
                    isFilter={false}
                    selectedDriver={selectedDriver}
                    setSelectedDriver={setSelectedDriver}
                />
            );
        }
    };

    const personNoteText = () => {
        if (selectedReciver === t('user'))
            return t('selectReciverNoteUser');
        else if (selectedReciver === t('page'))
            return t('selectReciverNotePage');
        else if (selectedReciver === t('driver'))
            return t('selectReciverNoteDriver');

    };


    return (
        <Box sx={{
            p: isTablet ? 1 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>

            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    p: isTablet ? 2 : 4
                }}
            >

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Typography id="transition-modal-title" variant="h6" component="h2">
                        {t('addNotification')}
                    </Typography>
                </Box>

                {/* dropdowns */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {/* reciver */}
                    <Box width={isTablet ? '100%' : '32%'}>
                        <ReciverDropdown
                            helperText={addErrors.reciver}
                            error={!!addErrors.reciver}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedReciver={selectedReciver}
                            setSelectedReciver={setSelectedReciver}
                        />
                    </Box>


                    {/* person */}
                    <Box width={isTablet ? '100%' : '32%'}>
                        {personDropdown()}
                    </Box>

                    {/* note */}
                    <Box width={isTablet ? '100%' : '32%'} mt={isTablet ? 1 : 4}>
                        <Typography id="transition-modal-title" variant="subtitle1" component="h2">
                            {personNoteText()}
                        </Typography>
                    </Box>
                </Box>


                {/* three titles */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                }}>
                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('titleKurdish')}
                            placeholder={t('titleKurdishHere')}
                            name="titleKurdish"
                            type='text'
                            value={formValues.titleKurdish}
                            error={!!addErrors.titleKurdish}
                            helperText={addErrors.titleKurdish}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('titleEnglish')}
                            placeholder={t('titleEnglishHere')}
                            name="titleEnglish"
                            type='text'
                            value={formValues.titleEnglish}
                            error={!!addErrors.titleEnglish}
                            helperText={addErrors.titleEnglish}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('titleArabic')}
                            placeholder={t('titleArabicHere')}
                            name="titleArabic"
                            type='text'
                            value={formValues.titleArabic}
                            error={!!addErrors.titleArabic}
                            helperText={addErrors.titleArabic}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {/* three decriptions */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                }}>
                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('descriptionKurdish')}
                            placeholder={t('descriptionKurdishHere')}
                            name="descriptionKurdish"
                            type='text'
                            value={formValues.descriptionKurdish}
                            error={!!addErrors.descriptionKurdish}
                            helperText={addErrors.descriptionKurdish}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('descriptionEnglish')}
                            placeholder={t('descriptionEnglishHere')}
                            name="descriptionEnglish"
                            type='text'
                            value={formValues.descriptionEnglish}
                            error={!!addErrors.descriptionEnglish}
                            helperText={addErrors.descriptionEnglish}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('descriptionArabic')}
                            placeholder={t('descriptionArabicHere')}
                            name="descriptionArabic"
                            type='text'
                            value={formValues.descriptionArabic}
                            error={!!addErrors.descriptionArabic}
                            helperText={addErrors.descriptionArabic}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {addErrors.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {addErrors.general}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: addErrors.general ? 2 : 4
                }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                    >
                        {isLoading ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('add')}</p>
                                <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddNotification;